import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  InputAdornment,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { getMappingCandidates } from '../../services/catalogService';

const OMOP_SUGGESTIONS_TABLE_COPY = {
  de: {
    noSuggestions: 'Keine Kandidaten verfügbar',
    loadingCandidates: 'Kandidaten werden geladen…',
    loadError: 'Kandidaten konnten nicht geladen werden.',
    suggestionsCount: '{{filtered}} von {{total}} Vorschlägen',
    savedCount: '{{count}} gespeichert',
    resetFilter: 'Filter zurücksetzen',
    rank: 'Rang',
    conceptName: 'Concept Name',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulary',
    domain: 'Domain',
    conceptClass: 'Concept-Klasse',
    standardConcept: 'Standard',
    standardYes: 'Standard',
    standardNo: 'Nicht-Standard',
    standardUnknown: 'Unbekannt',
    mapsToStandard: '→ Standard-Konzept: {{target}}',
    originDeterministic: 'Regelbasiert',
    originModel: 'KI-Vorschlag',
    originUnknown: 'Herkunft unbekannt',
    score: 'Score',
    pinColumn: 'Pin',
    action: 'Aktion',
    current: 'Aktuell',
    confidence: 'KI-Konfidenz: {{percent}}%',
    openInAthena: 'In ATHENA anzeigen',
    pin: 'Vorschlag speichern',
    unpin: 'Nicht mehr speichern',
    inUse: 'In Verwendung',
    propose: 'Vorschlagen',
    proposalTitle: 'Vorschlag: {{name}}',
    proposalHelp: '{{vocabulary}} {{code}} als alternatives Mapping vorschlagen',
    proposalPlaceholder: 'Begründung (optional): Warum ist dieses Concept besser geeignet?',
    submit: 'Einreichen',
    cancel: 'Abbrechen',
    suggestionReason: '💡 Warum dieser Vorschlag?',
    noResults: 'Keine Ergebnisse für "{{query}}"',
  },
  en: {
    noSuggestions: 'No candidates available',
    loadingCandidates: 'Loading candidates…',
    loadError: 'Could not load candidates.',
    suggestionsCount: '{{filtered}} of {{total}} suggestions',
    savedCount: '{{count}} saved',
    resetFilter: 'Reset filter',
    rank: 'Rank',
    conceptName: 'Concept name',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulary',
    domain: 'Domain',
    conceptClass: 'Concept class',
    standardConcept: 'Standard',
    standardYes: 'Standard',
    standardNo: 'Non-standard',
    standardUnknown: 'Unknown',
    mapsToStandard: '→ Standard concept: {{target}}',
    originDeterministic: 'Rule-based',
    originModel: 'AI suggestion',
    originUnknown: 'Origin unknown',
    score: 'Score',
    pinColumn: 'Pin',
    action: 'Action',
    current: 'Current',
    confidence: 'AI confidence: {{percent}}%',
    openInAthena: 'Open in ATHENA',
    pin: 'Save suggestion',
    unpin: 'Remove saved suggestion',
    inUse: 'In use',
    propose: 'Propose',
    proposalTitle: 'Proposal: {{name}}',
    proposalHelp: 'Suggest {{vocabulary}} {{code}} as an alternative mapping',
    proposalPlaceholder: 'Justification (optional): Why is this concept more suitable?',
    submit: 'Submit',
    cancel: 'Cancel',
    suggestionReason: '💡 Why this suggestion?',
    noResults: 'No results for "{{query}}"',
  },
  pt: {
    noSuggestions: 'Nenhum candidato disponível',
    loadingCandidates: 'A carregar candidatos…',
    loadError: 'Não foi possível carregar os candidatos.',
    suggestionsCount: '{{filtered}} de {{total}} sugestões',
    savedCount: '{{count}} guardados',
    resetFilter: 'Repor filtro',
    rank: 'Posição',
    conceptName: 'Nome do conceito',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulário',
    domain: 'Domínio',
    conceptClass: 'Classe do conceito',
    standardConcept: 'Padrão',
    standardYes: 'Padrão',
    standardNo: 'Não padrão',
    standardUnknown: 'Desconhecido',
    mapsToStandard: '→ Conceito padrão: {{target}}',
    originDeterministic: 'Baseado em regras',
    originModel: 'Sugestão de IA',
    originUnknown: 'Origem desconhecida',
    score: 'Pontuação',
    pinColumn: 'Pin',
    action: 'Ação',
    current: 'Atual',
    confidence: 'Confiança da IA: {{percent}}%',
    openInAthena: 'Abrir no ATHENA',
    pin: 'Guardar sugestão',
    unpin: 'Remover sugestão guardada',
    inUse: 'Em uso',
    propose: 'Propor',
    proposalTitle: 'Proposta: {{name}}',
    proposalHelp: 'Propor {{vocabulary}} {{code}} como mapeamento alternativo',
    proposalPlaceholder: 'Justificação (opcional): Porque é que este conceito é mais adequado?',
    submit: 'Submeter',
    cancel: 'Cancelar',
    suggestionReason: '💡 Porque esta sugestão?',
    noResults: 'Nenhum resultado para "{{query}}"',
  },
};

// Candidates can arrive from the real /api/mapping/candidates response (snake_case)
// or already normalized upstream (camelCase) -- support both.
const getConceptClass = (candidate) =>
  candidate?.conceptClass ?? candidate?.concept_class ?? candidate?.concept_class_id ?? '';

const getStandardConceptFlag = (candidate) =>
  candidate?.standardConcept ?? candidate?.standard_concept ?? null;

// OMOP CDM: standard_concept is 'S' for standard concepts and NULL for the
// common case of non-standard ones -- NULL is not "unknown", it IS the
// non-standard signal. Gating the badge on the flag being present made the
// main case (NULL) render as a bare "--" with no label and no reference to
// maps_to_standard (#98 round 2, B3).
//
// That NULL-is-non-standard rule only holds when the field was sent at all.
// A candidate object that never carries `standardConcept`/`standard_concept`
// as a key is a different situation -- the endpoint said nothing, not "NULL"
// -- and defaulting it to "Nicht-Standard" is a claim the data never made
// (Rueckmeldung Runde 1). `in` distinguishes "key present with value null"
// from "key absent" where `??` chaining alone cannot.
const hasStandardConceptField = (candidate) =>
  candidate != null && ('standardConcept' in candidate || 'standard_concept' in candidate);

const getStandardConceptState = (candidate) => {
  if (!hasStandardConceptField(candidate)) return 'unknown';
  return getStandardConceptFlag(candidate) === 'S' ? 'standard' : 'non-standard';
};

// Deterministic matches routinely carry no score at all --
// transformMappingCandidate normalizes that absence to `score: null`
// (catalogService.js). `candidate.score || 0` then rendered "0%", inventing a
// confidence value the endpoint never sent (Rueckmeldung Runde 1). A missing
// score gets its own "--" instead of a fabricated number.
const hasScore = (candidate) => typeof candidate?.score === 'number';

const getMapsToStandard = (candidate) =>
  candidate?.mapsToStandard ?? candidate?.maps_to_standard ?? null;

const getMapsToStandardLabel = (candidate) =>
  candidate?.mapsToStandardName ?? getMapsToStandard(candidate) ?? '';

// Whether a fetch is about to happen once the effect below runs. Exported so
// the "no flash of the empty state before loading" regression (#98 round 2)
// can be asserted directly against this rule instead of against React's
// effect-flushing timing, which React Testing Library's `render()` can mask.
export const shouldStartLoading = (usesSuppliedCandidates, parameterId) =>
  !usesSuppliedCandidates && Boolean(parameterId);

const DETERMINISTIC_ORIGINS = new Set(['deterministic', 'rule', 'rule-based', 'exact']);
const MODEL_ORIGINS = new Set(['model', 'ai', 'ai-suggestion', 'llm', 'agent']);

// Three states, not two: an origin string that matches neither known set is
// NOT the same as no origin at all. Defaulting an unrecognized string to
// "model" mislabels every row the moment the real endpoint spells its origin
// field differently than assumed here (#98 round 2, B5) -- so unrecognized
// strings get their own "unknown" state instead of silently becoming a claim.
//
// Round 3, B5: since OmopMappingDetail now passes only `parameterId`, the
// only source of a candidate is GET /api/mapping/candidates -- so its field
// names, not agent3Output's, decide the label. Read isDeterministic/origin in
// both camelCase and the snake_case a Django endpoint would actually send
// (is_deterministic, match_type), or every row silently defaults to "model".
//
// Round 4 (Rueckmeldung Runde 1): a candidate that carries NO origin signal
// at all used to default to "model" too, on the assumption that the only
// source was agent3Output, which never set one. That assumption no longer
// holds -- the only source is the real endpoint now, and a missing field
// there just means the endpoint said nothing about provenance. Defaulting
// that to "KI-Vorschlag" is a claim the data never made, so absence maps to
// "unknown" instead. An explicit `isDeterministic: false` is a real signal
// (not an absence) and still resolves to "model".
const getIsDeterministicFlag = (candidate) =>
  candidate?.isDeterministic ?? candidate?.is_deterministic;

const getOriginValue = (candidate) =>
  candidate?.origin ?? candidate?.matchOrigin ?? candidate?.match_origin
    ?? candidate?.matchType ?? candidate?.match_type;

const getOriginKind = (candidate) => {
  const isDeterministicFlag = getIsDeterministicFlag(candidate);
  if (isDeterministicFlag === true) return 'deterministic';

  const origin = getOriginValue(candidate);
  if (typeof origin === 'string') {
    const normalized = origin.toLowerCase();
    if (DETERMINISTIC_ORIGINS.has(normalized)) return 'deterministic';
    if (MODEL_ORIGINS.has(normalized)) return 'model';
    return 'unknown';
  }

  if (isDeterministicFlag === false) return 'model';

  return 'unknown';
};

/**
 * OmopSuggestionsTable Component
 * Displays OMOP concept candidates (deterministic matches and AI/model suggestions)
 * in a sortable, searchable table.
 * Allows users to pin suggestions and submit change proposals inline
 *
 * @param {object} props
 * @param {array} [props.candidates] - Pre-fetched concept candidates. Takes precedence over parameterId.
 * @param {string} [props.parameterId] - Parameter to fetch candidates for via GET /api/mapping/candidates (used when candidates is not supplied)
 * @param {string} props.currentTopMatchId - Concept ID of currently selected top match
 * @param {array} props.pinnedConceptIds - Array of user-pinned concept IDs
 * @param {function} props.onSelectCandidate - Callback when user selects a candidate (direct change for Data Stewards)
 * @param {function} props.onSubmitProposal - Callback when user submits a change proposal ({ candidate, justification })
 * @param {function} props.onTogglePin - Callback when user pins/unpins a candidate
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {string} props.searchQuery - External search query (optional)
 * @param {function} props.onSearchChange - Callback when search changes (optional)
 */
const OmopSuggestionsTable = ({
  candidates,
  parameterId,
  currentTopMatchId,
  pinnedConceptIds = [],
  onSelectCandidate,
  onSubmitProposal,
  onTogglePin,
  isProcessing = false,
  searchQuery: externalSearchQuery,
  onSearchChange
}) => {
  const { i18n } = useTranslation();
  // State for table controls
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const [orderBy, setOrderBy] = useState('score');
  const [order, setOrder] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  // State for proposal form
  const [expandedProposalRow, setExpandedProposalRow] = useState(null);
  const [proposalJustification, setProposalJustification] = useState('');
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_SUGGESTIONS_TABLE_COPY[language];

  // When no candidates are supplied by the caller, fetch them ourselves from
  // the real candidate source. Callers that already pass `candidates` keep
  // working unchanged (backward compatible).
  const usesSuppliedCandidates = candidates !== undefined;
  const [fetchedCandidates, setFetchedCandidates] = useState([]);
  // Start "loading" whenever a fetch will actually happen, otherwise the
  // empty state flashes before the effect below gets to run after paint.
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(
    shouldStartLoading(usesSuppliedCandidates, parameterId)
  );
  const [candidatesError, setCandidatesError] = useState(null);

  useEffect(() => {
    if (!shouldStartLoading(usesSuppliedCandidates, parameterId)) {
      return undefined;
    }

    let cancelled = false;
    setIsLoadingCandidates(true);
    setCandidatesError(null);

    getMappingCandidates(parameterId).then(({ candidates: fetched, source, error }) => {
      if (cancelled) return;
      if (source === 'error') {
        setCandidatesError(error || new Error('Candidates could not be loaded'));
      } else {
        setFetchedCandidates(fetched);
      }
      setIsLoadingCandidates(false);
    });

    return () => {
      cancelled = true;
    };
  }, [usesSuppliedCandidates, parameterId]);

  const effectiveCandidates = usesSuppliedCandidates ? candidates : fetchedCandidates;

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  // Filter candidates based on search query
  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) return effectiveCandidates;

    const query = searchQuery.toLowerCase();
    return effectiveCandidates.filter(candidate =>
      candidate.conceptName?.toLowerCase().includes(query) ||
      candidate.conceptId?.toString().includes(query) ||
      candidate.conceptCode?.toLowerCase().includes(query) ||
      candidate.vocabulary?.toLowerCase().includes(query)
    );
  }, [effectiveCandidates, searchQuery]);

  // Sort candidates
  const sortedCandidates = useMemo(() => {
    const sorted = [...filteredCandidates];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'rank':
          aValue = a.rank || 999;
          bValue = b.rank || 999;
          break;
        case 'score':
          // A missing score (deterministic matches routinely have none) is not
          // the same as a score of exactly 0 -- `a.score || 0` conflated the
          // two and sank every rule-based hit below any scored model
          // suggestion, regardless of rank (Rueckmeldung Runde 2, B1). A rule-
          // based match is not a probabilistic guess, so its absent score is
          // treated as maximally confident and sorts to the "best" end in
          // both directions, symmetric with hasScore() in the score cell.
          aValue = hasScore(a) ? a.score : Number.POSITIVE_INFINITY;
          bValue = hasScore(b) ? b.score : Number.POSITIVE_INFINITY;
          break;
        case 'name':
          aValue = a.conceptName || '';
          bValue = b.conceptName || '';
          break;
        case 'vocabulary':
          aValue = a.vocabulary || '';
          bValue = b.vocabulary || '';
          break;
        case 'domain':
          aValue = a.domain || '';
          bValue = b.domain || '';
          break;
        default:
          return 0;
      }

      // Handle string vs number comparison
      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return order === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return sorted;
  }, [filteredCandidates, orderBy, order]);

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle row expand
  const handleExpandRow = (conceptId) => {
    setExpandedRow(expandedRow === conceptId ? null : conceptId);
  };

  // Check if concept is pinned
  const isPinned = (conceptId) => {
    const lookupId = String(conceptId ?? '');
    return pinnedConceptIds.some(id => String(id ?? '') === lookupId);
  };

  // Check if concept is current top match
  const isCurrentTopMatch = (conceptId) => {
    return String(conceptId ?? '') === String(currentTopMatchId ?? '');
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 0.9) return 'success.main';
    if (score >= 0.7) return 'warning.main';
    return 'text.secondary';
  };

  // Handle proposal form open/close
  const handleOpenProposalForm = (conceptId) => {
    setExpandedProposalRow(conceptId);
    setProposalJustification('');
  };

  const handleCloseProposalForm = () => {
    setExpandedProposalRow(null);
    setProposalJustification('');
  };

  // Handle proposal submission
  const handleSubmitProposal = async (candidate) => {
    if (onSubmitProposal) {
      const result = await onSubmitProposal({
        candidate,
        justification: proposalJustification
      });

      if (result === false || result === null) {
        return;
      }
    }

    handleCloseProposalForm();
  };

  if (isLoadingCandidates) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {copy.loadingCandidates}
        </Typography>
      </Paper>
    );
  }

  if (candidatesError) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          {copy.loadError}
        </Typography>
      </Paper>
    );
  }

  // Empty candidate space is its own state, not an empty table.
  if (effectiveCandidates.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {copy.noSuggestions}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Results Count */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {copy.suggestionsCount
            .replace('{{filtered}}', filteredCandidates.length)
            .replace('{{total}}', effectiveCandidates.length)}
          {pinnedConceptIds.length > 0 && ` • ${copy.savedCount.replace('{{count}}', pinnedConceptIds.length)}`}
        </Typography>
        {searchQuery && (
          <Button size="small" onClick={() => handleSearchChange('')}>
            {copy.resetFilter}
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width={40} />
              <TableCell width={60}>
                <TableSortLabel
                  active={orderBy === 'rank'}
                  direction={orderBy === 'rank' ? order : 'asc'}
                  onClick={() => handleRequestSort('rank')}
                >
                  {copy.rank}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  {copy.conceptName}
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>{copy.conceptId}</TableCell>
              <TableCell width={100}>
                <TableSortLabel
                  active={orderBy === 'vocabulary'}
                  direction={orderBy === 'vocabulary' ? order : 'asc'}
                  onClick={() => handleRequestSort('vocabulary')}
                >
                  {copy.vocabulary}
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>
                <TableSortLabel
                  active={orderBy === 'domain'}
                  direction={orderBy === 'domain' ? order : 'asc'}
                  onClick={() => handleRequestSort('domain')}
                >
                  {copy.domain}
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>{copy.conceptClass}</TableCell>
              <TableCell width={110} align="center">{copy.standardConcept}</TableCell>
              <TableCell width={80} align="center">
                <TableSortLabel
                  active={orderBy === 'score'}
                  direction={orderBy === 'score' ? order : 'asc'}
                  onClick={() => handleRequestSort('score')}
                >
                  {copy.score}
                </TableSortLabel>
              </TableCell>
              <TableCell width={50} align="center">
                <Tooltip title="ATHENA">
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    borderRadius: 1,
                    p: 0.5,
                    width: 24,
                    height: 24
                  }}>
                    <Box
                      component="img"
                      src="/athena-favicon.ico"
                      alt="ATHENA"
                      sx={{ width: 14, height: 14 }}
                    />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell width={60} align="center">{copy.pinColumn}</TableCell>
              <TableCell width={120} align="center">{copy.action}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCandidates.map((candidate, idx) => {
              const isTop = isCurrentTopMatch(candidate.conceptId);
              const pinned = isPinned(candidate.conceptId);
              const isExpanded = expandedRow === candidate.conceptId;
              const hasReason = candidate.reason && candidate.reason.trim().length > 0;
              const standardConceptState = getStandardConceptState(candidate);
              const originKind = getOriginKind(candidate);
              const originLabel = originKind === 'deterministic'
                ? copy.originDeterministic
                : originKind === 'model'
                  ? copy.originModel
                  : copy.originUnknown;
              const originColor = originKind === 'deterministic'
                ? 'default'
                : originKind === 'model'
                  ? 'secondary'
                  : 'warning';

              return (
                <React.Fragment key={candidate.conceptId || idx}>
                  <TableRow
                    sx={{
                      backgroundColor: isTop ? '#e8f5e9' : pinned ? '#fff3e0' : 'inherit',
                      '&:hover': {
                        backgroundColor: isTop ? '#c8e6c9' : pinned ? '#ffe0b2' : '#f5f5f5'
                      }
                    }}
                  >
                    {/* Expand Button */}
                    <TableCell>
                      {hasReason && (
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(candidate.conceptId)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>

                    {/* Rank */}
                    <TableCell>
                      <Chip
                        label={`#${candidate.rank || (idx + 1)}`}
                        size="small"
                        color={isTop ? "success" : "default"}
                        variant={isTop ? "filled" : "outlined"}
                      />
                    </TableCell>

                    {/* Concept Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={isTop ? "bold" : "regular"}>
                          {candidate.conceptName}
                        </Typography>
                        {isTop && (
                          <Chip label={copy.current} size="small" color="success" sx={{ height: 20 }} />
                        )}
                        {/* Origin must be visible in the row itself, not only in a tooltip */}
                        <Chip
                          label={originLabel}
                          size="small"
                          variant="outlined"
                          color={originColor}
                          sx={{ height: 20 }}
                        />
                      </Box>
                    </TableCell>

                    {/* Concept ID */}
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {candidate.conceptId}
                      </Typography>
                    </TableCell>

                    {/* Vocabulary */}
                    <TableCell>
                      <Chip label={candidate.vocabulary} size="small" variant="outlined" />
                    </TableCell>

                    {/* Domain -- no invented fallback: an empty/missing value
                        renders as "unknown" (same as concept class below),
                        not as a guessed domain (Rueckmeldung Runde 1). */}
                    <TableCell>
                      <Typography variant="body2">
                        {candidate.domain || '—'}
                      </Typography>
                    </TableCell>

                    {/* Concept Class */}
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {getConceptClass(candidate) || '—'}
                      </Typography>
                    </TableCell>

                    {/* Standard concept + reference to the mapped standard concept.
                        No presence-gate on the *value* here: OMOP's NULL for
                        standard_concept IS the non-standard signal, not a missing
                        value (#98 round 2, B3). But a candidate that never carries
                        the field at all is a third case -- the endpoint said
                        nothing -- and gets its own "unknown" label instead of
                        silently becoming "Nicht-Standard" (Rueckmeldung Runde 1). */}
                    <TableCell align="center">
                      <Box>
                        <Chip
                          data-testid="standard-concept-chip"
                          label={
                            standardConceptState === 'standard'
                              ? copy.standardYes
                              : standardConceptState === 'non-standard'
                                ? copy.standardNo
                                : copy.standardUnknown
                          }
                          size="small"
                          variant="outlined"
                          color={
                            standardConceptState === 'standard'
                              ? 'success'
                              : standardConceptState === 'non-standard'
                                ? 'warning'
                                : 'default'
                          }
                        />
                        {standardConceptState === 'non-standard' && getMapsToStandard(candidate) && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                            {copy.mapsToStandard.replace('{{target}}', getMapsToStandardLabel(candidate))}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Score -- no score at all (deterministic matches) is not
                        the same as a score of 0 and must not render "0%". */}
                    <TableCell align="center">
                      {hasScore(candidate) ? (
                        <Tooltip title={copy.confidence.replace('{{percent}}', Math.round(candidate.score * 100))}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: getScoreColor(candidate.score) }}
                          >
                            {Math.round(candidate.score * 100)}%
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary" data-testid="score-missing">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* ATHENA Link */}
                    <TableCell align="center">
                      <Tooltip title={copy.openInAthena}>
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${candidate.conceptId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Pin Button */}
                    <TableCell align="center">
                      <Tooltip title={pinned ? copy.unpin : copy.pin}>
                        <IconButton
                          size="small"
                          onClick={() => onTogglePin && onTogglePin(candidate)}
                          disabled={isProcessing}
                          color={pinned ? "primary" : "default"}
                        >
                          {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Action Button */}
                    <TableCell align="center">
                      {isTop ? (
                        <Chip
                          label={copy.inUse}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => handleOpenProposalForm(candidate.conceptId)}
                          disabled={isProcessing}
                        >
                          {copy.propose}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Proposal Form */}
                  {expandedProposalRow === candidate.conceptId && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, backgroundColor: '#f9f9f9', border: '2px solid #1976d2', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {copy.proposalTitle.replace('{{name}}', candidate.conceptName)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                              {copy.proposalHelp
                                .replace('{{vocabulary}}', candidate.vocabulary)
                                .replace('{{code}}', candidate.conceptCode)}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              {/* Justification TextField */}
                              <TextField
                                size="small"
                                placeholder={copy.proposalPlaceholder}
                                value={proposalJustification}
                                onChange={(e) => setProposalJustification(e.target.value)}
                                multiline
                                rows={2}
                                sx={{ flexGrow: 1 }}
                              />

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => void handleSubmitProposal(candidate)}
                                >
                                  {copy.submit}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleCloseProposalForm}
                                >
                                  {copy.cancel}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Expanded Row - Show Reason */}
                  {hasReason && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 6, pr: 2, backgroundColor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <InfoIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                              <Box>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  {copy.suggestionReason}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {candidate.reason}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredCandidates.length === 0 && (
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {copy.noResults.replace('{{query}}', searchQuery)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default OmopSuggestionsTable;
